import {defineStore} from 'pinia';
import * as THREE from "three";
import {CubeReflectionMapping, SRGBColorSpace} from "three/src/constants";
import {route} from "ziggy-js";
import axios from "axios";

export type PreviewImageExportOptions = {
    transparent?: boolean;
    scale?: number;
};

interface SceneStoreState {
    scene: any | null;
    renderer: any | null;
    initialPreviewExporter: null | (() => Promise<void>);
    generatedImagesExporter: null | ((options: { count: number; transparent: boolean }) => Promise<void>);
    previewImageExporter: null | ((options?: PreviewImageExportOptions) => Promise<Blob | null>);
    modelAvailable: boolean;
    product: string | null;
    sceneState: {
        textureUrl?: string | null;
    } | null;
    layers: any[];
    textureUrl: string | null;
    loading: boolean;
    error: string | null;
    lightingSettings: any | null;
    cameraSettings: any | null;
    currentCameraView: any | null;
    lightingSource: 'model' | 'team' | null;
}

export const useSceneStore = defineStore('scene', {
    state: (): SceneStoreState => ({
        scene: null,
        renderer: null,
        initialPreviewExporter: null,
        generatedImagesExporter: null,
        previewImageExporter: null,
        modelAvailable: false,
        product: null,
        sceneState: null,
        layers: [],
        textureUrl: null,
        loading: false,
        error: null,
        lightingSettings: null,
        cameraSettings: null,
        currentCameraView: null,
        lightingSource: null,
    }),
    getters: {
        isLoading: (state: SceneStoreState): boolean => {
            return state.loading;
        },
    },
    actions: {
        setError(error: string) {
            this.error = error;
        },
        setProduct(product: string | null) {
            this.product = product;
        },
        setScene(newScene: any) {
            this.scene = newScene;
        },
        setIsLoading(isLoading: boolean) {
            this.loading = isLoading;
        },
        setRenderer(newRenderer: any) {
            this.renderer = newRenderer;
        },
        setInitialPreviewExporter(exporter: null | (() => Promise<void>)) {
            this.initialPreviewExporter = exporter;
        },
        setGeneratedImagesExporter(exporter: null | ((options: { count: number; transparent: boolean }) => Promise<void>)) {
            this.generatedImagesExporter = exporter;
        },
        setPreviewImageExporter(exporter: null | ((options?: PreviewImageExportOptions) => Promise<Blob | null>)) {
            this.previewImageExporter = exporter;
        },
        setModelAvailable(available: boolean) {
            this.modelAvailable = available;
        },
        async exportPreviewImage(options: PreviewImageExportOptions = {}): Promise<Blob | null> {
            if (typeof this.previewImageExporter === 'function') {
                return await this.previewImageExporter(options);
            }

            return null;
        },
        async saveInitialPreview() {
            if (typeof this.initialPreviewExporter === 'function') {
                await this.initialPreviewExporter();
            }
        },
        async saveGeneratedImages(options: { count: number; transparent: boolean }) {
            if (typeof this.generatedImagesExporter === 'function') {
                await this.generatedImagesExporter(options);
                return;
            }

            throw new Error('Generated image exporter is not ready');
        },
        setTextureUrl(newTextureUrl: string | null) {
            this.textureUrl = newTextureUrl;
        },
        setLightingSettings(settings: any) {
            this.lightingSettings = settings;
        },
        setCameraSettings(settings: any) {
            this.cameraSettings = settings;
        },
        setCurrentCameraView(view: any | null) {
            this.currentCameraView = view;
        },
        setLightingSource(source: 'model' | 'team' | null) {
            this.lightingSource = source;
        },
        setBackgroundUrl(url: string) {
            // This action is now handled by the ThreeJs component listening via $onAction
        },
        async fetchProduct(productId: string|number) {
            this.loading = true;
            this.error = null;
            try {
                // Haal het product op via de juiste API endpoint (API route, niet de inertia blade route)
                const response = await axios.get(route('admin.api.products.show', { product: productId }));
                this.setProduct(response.data.product);
            } catch (e) {
                console.error('Error fetching product:', e);
                this.error = 'Failed to fetch product ';
            } finally {
                this.loading = false;
            }
        },
    },
});
